"use client";

import { createContext, useContext, useEffect, useState } from "react";
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported,
} from "firebase/messaging";

import { firebaseApp } from "@/utils/firebase/firebase-client";
import {
  subscribeFCMToken,
  unsubscribeFCMToken,
} from "@/app/api/notificationapi";

interface FCMContextType {
  permission: NotificationPermission;
  loading: boolean;
  serviceWorkerReady: boolean;
  token: string | null;
  isNotificationEnabled: boolean;

  enableNotifications: () => Promise<void>;
  disableNotification: () => Promise<void>;
}

const FCMContext = createContext<FCMContextType | undefined>(undefined);

export const useFCM = () => {
  const context = useContext(FCMContext);

  if (!context) {
    throw new Error("useFCM must be used inside FCMProvider");
  }

  return context;
};
export default function FCMProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [permission, setPermission] =
    useState<NotificationPermission>("default");

  const [loading, setLoading] = useState(false);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);

  useEffect(() => {
    const initializeNotifications = async () => {
      try {
        if (typeof window === "undefined") return;

        if (!("serviceWorker" in navigator)) {
          console.error("Service Worker is not supported");
          return;
        }

        if (!("Notification" in window)) {
          console.error("Notifications are not supported");
          return;
        }

        const currentPermission = Notification.permission;
        setPermission(currentPermission);
        const registration = await navigator.serviceWorker.register(
          "/fcm-sw.js",
          {
            scope: "/",
            updateViaCache: "none",
          },
        );

        await navigator.serviceWorker.ready;

        setServiceWorkerReady(true);
        if (currentPermission === "denied") {
          console.log("Notifications are blocked by the user.");
          return;
        }
        if (currentPermission === "default") {
          const newPermission = await Notification.requestPermission();

          setPermission(newPermission);

          if (newPermission !== "granted") {
            console.log(
              "Notification permission was not granted:",
              newPermission,
            );
            return;
          }
        }
        await initializeFCMToken(registration);
      } catch (error) {
        console.error("Notification initialization failed:", error);
      }
    };

    initializeNotifications();
  }, []);

  const initializeFCMToken = async (
    swRegistration: ServiceWorkerRegistration,
  ) => {
    try {
      setLoading(true);

      const messagingSupported = await isSupported();

      if (!messagingSupported) {
        console.error("Firebase Messaging is not supported in this browser");
        return;
      }

      const messaging = getMessaging(firebaseApp);

      const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

      if (!vapidKey) {
        throw new Error("NEXT_PUBLIC_FIREBASE_VAPID_KEY is missing");
      }

      const fcmToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      });

      if (!fcmToken) {
        throw new Error("FCM token was not generated");
      }

      console.log("FCM token:", fcmToken);
      const userAgent = navigator.userAgent;
      let browser = "unknown";

      if (userAgent.includes("Edg/") || userAgent.includes("Edge/")) {
        browser = "Edge";
      } else if (userAgent.includes("Chrome") && !userAgent.includes("Edg/")) {
        browser = "Chrome";
      } else if (userAgent.includes("Firefox")) {
        browser = "Firefox";
      } else if (
        userAgent.includes("Safari") &&
        !userAgent.includes("Chrome")
      ) {
        browser = "Safari";
      }

      // Save token in database
      const saveResponse = await subscribeFCMToken(
        fcmToken,
        undefined,
        browser,
      );

      if (!saveResponse?.success) {
        throw new Error(saveResponse?.message || "Failed to save FCM token");
      }

      setToken(fcmToken);
      setPermission("granted");
      setIsNotificationEnabled(true);

      console.log("FCM notifications enabled successfully");
    } catch (error) {
      console.error("Failed to initialize FCM:", error);
      setIsNotificationEnabled(false);
    } finally {
      setLoading(false);
    }
  };
  const enableNotifications = async () => {
    if (loading) return;
    try {
      setLoading(true);
      console.log("Starting FCM notification setup...");

      if (typeof window === "undefined") {
        return;
      }

      if (!("Notification" in window)) {
        console.error("Browser does not support notifications");
        return;
      }

      if (!("serviceWorker" in navigator)) {
        console.error("Browser does not support service workers");
        return;
      }

      const messagingSupported = await isSupported();

      if (!messagingSupported) {
        console.error("Firebase Messaging is not supported in this browser");
        return;
      }

      let currentPermission = Notification.permission;

      if (currentPermission === "default") {
        currentPermission = await Notification.requestPermission();
        setPermission(currentPermission);
      }

      if (currentPermission === "denied") {
        console.error("Notification permission is denied.");

        console.error(
          "Open browser site settings and allow notifications for this website.",
        );

        return;
      }

      if (currentPermission !== "granted") {
        console.error("Notification permission was not granted");
        return;
      }

      const swRegistration = await navigator.serviceWorker.ready;

      if (!swRegistration) {
        throw new Error("FCM Service Worker registration not available");
      }

      if (!swRegistration.active) {
        throw new Error("FCM Service Worker is not active");
      }

      const messaging = getMessaging(firebaseApp);

      const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY;

      if (!vapidKey) {
        throw new Error("NEXT_PUBLIC_FIREBASE_VAPID_KEY is missing");
      }

      const fcmToken = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: swRegistration,
      });

      if (!fcmToken) {
        throw new Error("FCM token was not generated");
      }

      const userAgent = navigator.userAgent;

      let browser = "unknown";

      if (userAgent.includes("Edg/")) {
        browser = "Edge";
      } else if (userAgent.includes("Chrome") && !userAgent.includes("Edg/")) {
        browser = "Chrome";
      } else if (userAgent.includes("Firefox")) {
        browser = "Firefox";
      } else if (
        userAgent.includes("Safari") &&
        !userAgent.includes("Chrome")
      ) {
        browser = "Safari";
      }
      const saveResponse = await subscribeFCMToken(
        fcmToken,
        undefined,
        browser,
      );

      if (!saveResponse?.success) {
        throw new Error(saveResponse?.message || "Failed to save FCM token");
      }
      setToken(fcmToken);
      setPermission("granted");
      setIsNotificationEnabled(true);
      console.log("FCM notifications enabled successfully");
    } catch (error) {
      console.error("Failed to enable FCM notifications:", error);
    } finally {
      setLoading(false);
    }
  };
  const disableNotification = async () => {
    if (loading) return;

    try {
      setLoading(true);

      if (!token) {
        console.log("No FCM token found");
        const supported = await isSupported();
        if (supported) {
          const messaging = getMessaging(firebaseApp);
          await deleteToken(messaging);
        }
        setIsNotificationEnabled(false);
        return;
      }
      const response = await unsubscribeFCMToken(token);
      if (!response?.success) {
        throw new Error(response?.message || "Failed to unsubscribe FCM token");
      }
      const supported = await isSupported();

      if (supported) {
        const messaging = getMessaging(firebaseApp);

        await deleteToken(messaging);
      }
      setToken(null);
      setIsNotificationEnabled(false);
      console.log("FCM notifications disabled successfully");
    } catch (error) {
      console.error("Failed to disable FCM notifications:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (permission !== "granted") {
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const setupForegroundMessaging = async () => {
      try {
        const supported = await isSupported();

        if (!supported) {
          console.log("Firebase Messaging is not supported");
          return;
        }

        const messaging = getMessaging(firebaseApp);

        unsubscribe = onMessage(messaging, (payload) => {
          const title = payload.notification?.title || "Nearnist";
          const body = payload.notification?.body || "";
          if (Notification.permission === "granted") {
            new Notification(title, {
              body,
              icon: "/android-chrome-192x192.png",
            });
          }
        });
      } catch (error) {
        console.error("Foreground FCM initialization failed:", error);
      }
    };

    setupForegroundMessaging();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [permission]);

  return (
    <>
      <FCMContext.Provider
        value={{
          permission,
          loading,
          serviceWorkerReady,
          token,
          isNotificationEnabled,
          enableNotifications,
          disableNotification,
        }}
      >
        {children}
      </FCMContext.Provider>

      {/* {(!isNotificationEnabled || permission !== "granted") && (
        <button
          type="button"
          onClick={enableNotifications}
          disabled={loading || permission === "denied"}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 99999,
            padding: "12px 20px",
            border: "none",
            borderRadius: "8px",
            background: permission === "denied" ? "#6b7280" : "#2563eb",
            color: "#fff",
            cursor:
              loading || permission === "denied" ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading
            ? "Enabling..."
            : permission === "denied"
            ? "Notifications Blocked"
            : "Enable Notifications"}
        </button>
      )}

      {permission === "granted" && isNotificationEnabled && (
        <button
          type="button"
          onClick={disableNotification}
          disabled={loading}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            zIndex: 99999,
            padding: "12px 20px",
            border: "none",
            borderRadius: "8px",
            background: "#dc2626",
            color: "#fff",
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.7 : 1,
          }}
        >
          {loading ? "Disabling..." : "Disable Notifications"}
        </button>
      )} */}
    </>
  );
}
